import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import guardianEmailService from "../services/guardianEmailService";
import Loader from "../components/Loader";
import styles from "./GuardianEmailStatusCreate.module.css";

const GuardianEmailStatusCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    status_id: "",
    subject: "",
    content: "",
    files: [],
    filesCounter: 1,
  });

  // Available statuses for dropdown
  const [statuses, setStatuses] = useState({});

  // Form errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        setLoading(true);
        console.log("🔍 [FRONTEND] Fetching guardian statuses");

        const response = await guardianEmailService.getGuardianStatuses();
        console.log("📊 [FRONTEND] Statuses response:", response);

        if (response.data && response.data.success) {
          setStatuses(response.data.data || {});
        } else {
          // Fallback to mock data if API fails
          setStatuses({
            "": "-- please select --",
            "1": "Active",
            "2": "Documents Required",
            "3": "Pending",
            "4": "Archived",
            "5": "Declined",
            "6": "Blacklisted",
            "7": "Previous Guardian",
          });
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ [FRONTEND] Error fetching statuses:", err);
        // Set mock data on error
        setStatuses({
          "": "-- please select --",
          "1": "Active",
          "2": "Documents Required",
          "3": "Pending",
          "4": "Archived",
          "5": "Declined",
          "6": "Blacklisted",
          "7": "Previous Guardian",
        });
        setLoading(false);
      }
    };

    fetchStatuses();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle programmatic value changes (for browser automation/testing)
  const handleInput = (e) => {
    const { name, value } = e.target;
    // Update state when input value changes (even if not via onChange)
    setFormData((prev) => {
      if (prev[name] !== value) {
        return {
          ...prev,
          [name]: value,
        };
      }
      return prev;
    });
  };

  const handleFileChange = (e, index) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      files: prev.files.map((file, i) => (i === index ? files[0] : file)),
    }));

    // Clear error when user selects a file
    if (errors[`files.${index}`]) {
      setErrors((prev) => ({
        ...prev,
        [`files.${index}`]: "",
      }));
    }
  };

  const addFileField = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, null],
      filesCounter: prev.filesCounter + 1,
    }));
  };

  const removeFileField = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      filesCounter: prev.filesCounter - 1,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Get current values from form inputs (in case React state is out of sync)
    const statusSelect = document.getElementById('status_id');
    const subjectInput = document.getElementById('subject');
    const contentInput = document.getElementById('content');

    const currentStatusId = statusSelect?.value || formData.status_id;
    const currentSubject = subjectInput?.value || formData.subject;
    const currentContent = contentInput?.value || formData.content;

    // Update formData if values differ (for programmatic changes)
    if (currentStatusId !== formData.status_id || 
        currentSubject !== formData.subject || 
        currentContent !== formData.content) {
      setFormData((prev) => ({
        ...prev,
        status_id: currentStatusId,
        subject: currentSubject,
        content: currentContent,
      }));
    }

    // Validate with current values
    if (!currentStatusId || currentStatusId === '') {
      newErrors.status_id = "Status is required";
    }

    if (!currentSubject || currentSubject.trim() === '') {
      newErrors.subject = "Subject is required";
    }

    if (!currentContent || currentContent.trim() === '') {
      newErrors.content = "Content is required";
    }

    // Validate files if any are selected (must match backend multer rules)
    const allowedExtensions = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
    formData.files.forEach((file, index) => {
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) {
        newErrors[`files.${index}`] = "File size must be less than 20MB";
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        newErrors[`files.${index}`] =
          "Invalid file type. Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get current values from DOM (in case React state is out of sync)
    const statusSelect = document.getElementById('status_id');
    const subjectInput = document.getElementById('subject');
    const contentInput = document.getElementById('content');

    const currentStatusId = statusSelect?.value || formData.status_id;
    const currentSubject = subjectInput?.value || formData.subject;
    const currentContent = contentInput?.value || formData.content;

    // Update formData with current values before validation
    const updatedFormData = {
      ...formData,
      status_id: currentStatusId,
      subject: currentSubject,
      content: currentContent,
    };

    // Validate with updated form data
    const newErrors = {};
    if (!currentStatusId || currentStatusId === '') {
      newErrors.status_id = "Status is required";
    }
    if (!currentSubject || currentSubject.trim() === '') {
      newErrors.subject = "Subject is required";
    }
    if (!currentContent || currentContent.trim() === '') {
      newErrors.content = "Content is required";
    }

    // Validate files if any are selected (must match backend multer rules)
    const allowedExtensions = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
    updatedFormData.files.forEach((file, index) => {
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) {
        newErrors[`files.${index}`] = "File size must be less than 20MB";
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        newErrors[`files.${index}`] =
          "Invalid file type. Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError("Please fix the errors before submitting");
      // Update formData state for next render
      setFormData(updatedFormData);
      return;
    }

    // Update formData state with synced values
    setFormData(updatedFormData);
    setErrors({});

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      console.log("🚀 [FRONTEND] Submitting email data:", updatedFormData);

      // Create FormData for file uploads
      const submitData = new FormData();
      submitData.append("status_id", updatedFormData.status_id);
      submitData.append("subject", updatedFormData.subject);
      submitData.append("content", updatedFormData.content);
      submitData.append("filesCounter", updatedFormData.filesCounter);

      // Append files
      updatedFormData.files.forEach((file, index) => {
        if (file) {
          submitData.append("files[]", file);
        }
      });

      const response = await guardianEmailService.sendEmailToGuardiansOfStatus(submitData);

      if (response.data && response.data.status === "success") {
        setSuccess(response.data.message || "You have sent the email!");
        console.log("✅ [FRONTEND] Email sent successfully");
        setTimeout(() => {
          navigate("/guardians");
        }, 2000);
      } else {
        throw new Error(response.data?.message || "Failed to send email");
      }
    } catch (err) {
      console.error("❌ [FRONTEND] Error sending email:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to send email";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>


      <div className={styles.mainContent}>

        {/* Breadcrumbs */}
        <div className={styles.breadcrumbRow}>
          <ol className={styles.breadcrumb}>
            <li>
              <Link to="/dashboard" className={styles.breadcrumbLink}>
                Dashboard
              </Link>
            </li>
            <li>
              <Link to="/guardians" className={styles.breadcrumbLink}>
                Guardians
              </Link>
            </li>
            <li className={styles.active}>
              Email guardians of status
            </li>
          </ol>
        </div>

        <div className={styles.panelForm}>
          <div className={styles.panelHeading}>
            <h3 className={styles.panelTitle}>Email guardians of status</h3>
          </div>
          <div className={styles.panelBody}>
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <fieldset className={styles.formHorizontal}>
                {/* ID to run JavaScript */}
                <div id="js-email-attachments"></div>

                {/* Status Section */}
                <div className={styles.titleStandard}>
                  <h3>Status</h3>
                </div>

                <div className={styles.panelGroup}>
                  <div className={styles.formGroup}>
                    <label
                      htmlFor="status_id"
                      className={`${styles.controlLabel} ${styles.colSm2} ${styles.labelRequired}`}
                    >
                      Status
                    </label>
                    <div className={styles.colSm3}>
                      <select
                        name="status_id"
                        id="status_id"
                        className={styles.formControl}
                        value={formData.status_id}
                        onChange={handleInputChange}
                        onInput={handleInput}
                        required
                      >
                        {Object.entries(statuses).map(([id, name]) => (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        ))}
                      </select>
                      {errors.status_id && (
                        <div className={styles.textDanger}>
                          {errors.status_id}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Email Section */}
                <div className={styles.titleStandard}>
                  <h3>Email</h3>
                </div>

                <div className={styles.panelGroup}>
                  <div className={styles.mb30}>
                    The email will be send to guardians who agreed to receive
                    promotional offers, available property lists and other updates
                    from Global Guardians and wanted to receive updates by email.
                  </div>

                  <div className={styles.formGroup}>
                    <label
                      htmlFor="subject"
                      className={`${styles.controlLabel} ${styles.colSm2} ${styles.labelRequired}`}
                    >
                      Subject
                    </label>
                    <div className={styles.colSm10}>
                      <input
                        type="text"
                        name="subject"
                        id="subject"
                        className={styles.formControl}
                        value={formData.subject}
                        onChange={handleInputChange}
                        onInput={handleInput}
                        required
                      />
                      {errors.subject && (
                        <div className={styles.textDanger}>
                          {errors.subject}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label
                      htmlFor="content"
                      className={`${styles.controlLabel} ${styles.colSm2} ${styles.labelRequired}`}
                    >
                      Content
                    </label>
                    <div className={styles.colSm10}>
                      <textarea
                        name="content"
                        id="content"
                        className={`${styles.formControl} ${styles.ckeditor}`}
                        value={formData.content}
                        onChange={handleInputChange}
                        onInput={handleInput}
                        rows="10"
                        required
                      />
                      {errors.content && (
                        <div className={styles.textDanger}>
                          {errors.content}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attachments Section */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm2}`}>
                      Attachments
                    </label>
                    <div className={styles.colSm5}>
                      <button
                        type="button"
                        className={styles.addFileFieldButton}
                        onClick={addFileField}
                        style={{ marginBottom: "10px" }}
                      >
                        Add file
                      </button>

                      <div className={styles.helpBlock} style={{ marginBottom: "10px" }}>
                        Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG · max 20MB per file
                      </div>

                      <input
                        type="hidden"
                        id="filesCounter"
                        name="filesCounter"
                        value={formData.filesCounter}
                      />

                      <div className={styles.colSm12}>
                        <div className={styles.formGroup}>
                          <div className={styles.inputFileFieldsWrap}>
                            {/* Initial file field */}
                            <div style={{ marginBottom: "3px" }}>
                              <input
                                type="file"
                                name="files[]"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                className={styles.formControl}
                                onChange={(e) => handleFileChange(e, 0)}
                              />
                            </div>

                            {/* Additional file fields */}
                            {formData.files.slice(1).map((file, index) => (
                              <div key={index + 1} style={{ marginBottom: "3px" }}>
                                <input
                                  type="file"
                                  name="files[]"
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                  className={styles.formControl}
                                  onChange={(e) => handleFileChange(e, index + 1)}
                                />
                                <a
                                  href="#"
                                  className={styles.removeField}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    removeFileField(index + 1);
                                  }}
                                >
                                  Remove
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* File validation errors */}
                        {formData.files.map((file, index) => (
                          errors[`files.${index}`] && (
                            <div key={index} className={styles.row}>
                              <div className={styles.alertDanger}>
                                {errors[`files.${index}`]}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>

              <fieldset className={styles.formHorizontal}>
                <div className={styles.formGroupTextRight}>
                  <div className={styles.colSm12}>
                    <button
                      type="submit"
                      className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles.preventMultipleRequests}`}
                      disabled={submitting}
                    >
                      {submitting ? "Sending..." : "Send email"}
                    </button>
                  </div>
                </div>
              </fieldset>
            </form>

            {/* Success/Error Messages */}
            {success && <div className={styles.alertSuccess}>{success}</div>}
            {error && <div className={styles.alertDanger}>{error}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianEmailStatusCreate; 